import { Box, MenuItem, Select, Stack, Typography } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useTranslation } from 'react-i18next';
import { tokens } from '../theme';

const MxFlag = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 7 4" width={20} height={14} aria-hidden="true" focusable="false">
    <rect width="7" height="4" fill="#ffffff" />
    <rect width="2.333" height="4" fill="#006847" />
    <rect x="4.667" width="2.333" height="4" fill="#ce1126" />
    <g transform="translate(3.5 2)" fill="none" stroke="#5b3a00" strokeWidth="0.12" strokeLinecap="round">
      <ellipse rx="0.55" ry="0.32" />
      <path d="M-0.35 0.05 Q-0.1 -0.3 0.15 -0.1 Q0.3 0 0.4 0.15" />
      <path d="M-0.15 -0.15 L-0.05 -0.05" />
    </g>
    <circle cx="3.5" cy="1.95" r="0.08" fill="#5b3a00" />
  </svg>
);

const UsFlag = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 19 10" width={20} height={14} aria-hidden="true" focusable="false">
    <rect width="19" height="10" fill="#ffffff" />
    <g fill="#b22234">
      <rect width="19" height="0.77" y="0" />
      <rect width="19" height="0.77" y="1.54" />
      <rect width="19" height="0.77" y="3.08" />
      <rect width="19" height="0.77" y="4.62" />
      <rect width="19" height="0.77" y="6.15" />
      <rect width="19" height="0.77" y="7.69" />
      <rect width="19" height="0.77" y="9.23" />
    </g>
    <rect width="7.6" height="5.38" fill="#3c3b6e" />
  </svg>
);

const LANGUAGES = [
  { value: 'es', short: 'ES', label: 'Español', Flag: MxFlag },
  { value: 'en', short: 'EN', label: 'English', Flag: UsFlag },
];

const LanguageToggle = () => {
  const { i18n } = useTranslation();
  const current = i18n.language?.startsWith('en') ? 'en' : 'es';

  return (
    <Select
      value={current}
      onChange={(event) => i18n.changeLanguage(event.target.value as string)}
      size="small"
      // MUI's default filled triangle reads heavier than anything else in the
      // header; a thin chevron sits with the type instead of shouting over it.
      IconComponent={KeyboardArrowDownIcon}
      // The trigger shows flag + code; the full language name lives in the list.
      // A 120px-wide control was pulling more weight than a two-option switch earns.
      renderValue={(value) => {
        const lang = LANGUAGES.find((l) => l.value === value) ?? LANGUAGES[0];
        return (
          <Stack direction="row" alignItems="center" spacing={0.875}>
            <Box
              component="span"
              sx={{ display: 'inline-flex', borderRadius: '2px', overflow: 'hidden', flexShrink: 0 }}
            >
              <lang.Flag />
            </Box>
            <Typography
              component="span"
              sx={{ fontSize: 14, fontWeight: 600, lineHeight: 1, letterSpacing: '0.02em' }}
            >
              {lang.short}
            </Typography>
          </Stack>
        );
      }}
      inputProps={{ 'aria-label': 'Idioma / Language' }}
      sx={{
        backgroundColor: tokens.color.white,
        borderRadius: `${tokens.radius.pill}px`,
        height: 40,

        // MUI reserves a flat 32px on the right for the caret and pads the top
        // and bottom independently, which left the flag/label pair sitting
        // off-centre inside a pill. Doubling the class beats that specificity so
        // the control can be balanced by hand: equal optical inset either side,
        // and the content vertically centred by flex rather than by padding.
        '& .MuiSelect-select.MuiSelect-select': {
          display: 'flex',
          alignItems: 'center',
          height: '100%',
          minHeight: 0,
          boxSizing: 'border-box',
          paddingTop: 0,
          paddingBottom: 0,
          paddingLeft: '14px',
          paddingRight: '30px',
        },

        '& .MuiSelect-icon': {
          right: 9,
          fontSize: 20,
          color: tokens.color.inkMuted,
          transition: `transform ${tokens.duration.control} ${tokens.ease.out}`,
        },

        '& fieldset': { borderColor: tokens.color.line },
        '&:hover fieldset': { borderColor: tokens.color.rose },
        '&.Mui-focused fieldset': { borderColor: tokens.color.roseInk },
      }}
      MenuProps={{
        PaperProps: {
          sx: {
            mt: 0.5,
            borderRadius: `${tokens.radius.sm}px`,
            boxShadow: tokens.shadow.md,
          },
        },
      }}
    >
      {LANGUAGES.map(({ value, label, Flag }) => (
        <MenuItem key={value} value={value} sx={{ gap: 1, py: 1 }}>
          <Box component="span" sx={{ display: 'inline-flex', borderRadius: '2px', overflow: 'hidden' }}>
            <Flag />
          </Box>
          <Typography sx={{ fontSize: 14 }}>{label}</Typography>
        </MenuItem>
      ))}
    </Select>
  );
};

export default LanguageToggle;
